import { ErrorObject, serializeError } from 'serialize-error';
import {
  ApiFault,
  ApiFaultOrWarnings,
  ApiFaultOrWarningsResponse
} from '../api/api.fault.interfaces';
import {
  HttpErrorResponse,
  HttpErrorResponseSerialized,
  HttpResponse,
  HttpResponseSerialized
} from '../interfaces';
import { clone, isEmpty } from '../utility/common';
import { CAPTURED_ERROR_BASE } from './errorHandling.config';
import {
  CapturedApiError,
  CapturedApiFaultError,
  CapturedError,
  CapturedRuntimeError,
  CapturedUnhandledPromiseRejectionError,
  DEFAULT_ERROR_SUBTYPE,
  ErrorSubtypes,
  ErrorTypes
} from './errorHandling.types';

/**
 * Pure conversion functions: raw errors → CapturedError hierarchy.
 *
 * Consolidates conversion logic that was previously spread across
 * api.error.helpers, api.fault.helpers, errorHandling.capturedError.helpers,
 * and errorHandling.hook (unhandled rejection handling).
 */
export class ErrorConverter {
  /**
   * Convert a raw HTTP error response (e.g. Axios error) into a
   * CapturedApiError with the appropriate subtype.
   *
   * Subtype classification:
   * - ECONNABORTED + "timeout" → TIMEOUT
   * - ECONNABORTED without "timeout" → NETWORK
   * - Empty response or "Network Error" → NETWORK
   * - Non-200 status with response body → SERVER
   * - Otherwise → UNKNOWN
   */
  static fromHttpError<T>(
    errorResponse: HttpErrorResponse<T>
  ): CapturedApiError<T> {
    let errorSubtype = DEFAULT_ERROR_SUBTYPE;
    const hasEmptyResponse = isEmpty(errorResponse);
    const serializedErrorResponse = serializeError(
      errorResponse
    ) as unknown as HttpErrorResponseSerialized;

    const {
      code,
      config = {},
      message = '',
      request,
      response,
      stack
    } = serializedErrorResponse;

    if (code === 'ECONNABORTED' && message?.includes('timeout')) {
      errorSubtype = ErrorSubtypes.TIMEOUT;
    } else if (code === 'ECONNABORTED') {
      errorSubtype = ErrorSubtypes.NETWORK;
    } else if (hasEmptyResponse || message?.includes('Network Error')) {
      errorSubtype = ErrorSubtypes.NETWORK;
    } else if (!hasEmptyResponse && response?.status !== 200) {
      errorSubtype = ErrorSubtypes.SERVER;
    }

    return {
      ...clone(CAPTURED_ERROR_BASE),
      applicationContext: {
        apiMessage: { config, request, response },
        moduleName: ''
      },
      errorContext: { code, message, stack },
      isBodyOrPayloadRedacted: false,
      message: message || '',
      subtype: errorSubtype,
      type: ErrorTypes.API
    } as CapturedApiError<T>;
  }

  /**
   * Convert a successful HTTP response containing an API fault (Core Services
   * 200 + Fault payload) into a CapturedApiFaultError.
   */
  static fromFaultResponse(
    response: HttpResponse<ApiFaultOrWarnings>
  ): CapturedApiFaultError {
    const serializedResponse = serializeError(
      response
    ) as unknown as HttpResponseSerialized<ApiFaultOrWarnings>;

    const {
      config = {},
      data,
      headers,
      request,
      status,
      statusText
    } = serializedResponse;

    const faultCode = data?.Fault?.Code;
    const faultSubcode = data?.Fault?.Subcode || undefined;
    const standardizedMessage = `Request failed ${
      faultCode ? `with fault code ${faultCode}` : ''
    }`;

    return {
      ...clone(CAPTURED_ERROR_BASE),
      applicationContext: {
        apiMessage: {
          config,
          request,
          response: { config, data, headers, status, statusText }
        },
        moduleName: ''
      },
      errorContext: {
        faultCode,
        message: data?.Fault?.Message || standardizedMessage,
        faultSubcode
      },
      isBodyOrPayloadRedacted: false,
      message: standardizedMessage,
      subtype: ErrorSubtypes.FAULT,
      type: ErrorTypes.API
    } as CapturedApiFaultError;
  }

  /**
   * Convert a runtime JavaScript error into a CapturedRuntimeError.
   * Uses serialize-error for safe serialization of Error objects.
   */
  static fromRuntimeError(error: Error | ErrorObject): CapturedRuntimeError {
    const serializedError = serializeError(error);
    const { message = '', name, stack } = serializedError;

    return {
      ...clone(CAPTURED_ERROR_BASE),
      errorContext: {
        columnNumber: 0,
        filename: '',
        lineNumber: 0,
        message,
        stack
      },
      message: `${name}: ${message}`,
      subtype: ErrorSubtypes.RUNTIME,
      type: ErrorTypes.APPLICATION
    } as CapturedRuntimeError;
  }

  /**
   * Convert an unhandled promise rejection event into a CapturedError.
   * If the rejection reason is already a captured error (isCaptured === true),
   * it passes through unchanged.
   */
  static fromUnhandledRejection(event: PromiseRejectionEvent): CapturedError {
    if (event.reason?.isCaptured) {
      return event.reason as CapturedError;
    }

    const capturedError = clone(
      CAPTURED_ERROR_BASE as CapturedUnhandledPromiseRejectionError
    );

    capturedError.errorContext = {
      message: event.reason?.message || event.reason,
      stack: event.reason
    };
    capturedError.isHandled = true;
    capturedError.subtype = ErrorSubtypes.UNHANDLED_PROMISE;
    capturedError.type = ErrorTypes.APPLICATION;

    return capturedError;
  }

  /**
   * Determine whether an error response is a raw HTTP error (as opposed to
   * an already-captured API fault error).
   */
  static isRawHttpError(
    error: CapturedApiFaultError | HttpErrorResponse
  ): error is HttpErrorResponse {
    return (error as CapturedApiFaultError).type === undefined;
  }

  // ─── Type Guards ────────────────────────────────────
  // Consolidated from errorHandling.capturedError.helpers

  /** Determine whether a captured error is an "API error". */
  static isCapturedErrorApiError(
    error: CapturedError
  ): error is CapturedApiError {
    return error.type === ErrorTypes.API;
  }

  /** Determine whether a captured API error is a Core Services "API fault error". */
  static isCapturedApiErrorCoreFaultError(
    error: CapturedApiError
  ): error is CapturedApiFaultError {
    return (
      (error as CapturedApiFaultError).type === ErrorTypes.API &&
      (error as CapturedApiFaultError).subtype === ErrorSubtypes.FAULT
    );
  }

  /** Determine whether a captured error is an "API fault error". */
  static isCapturedErrorApiFaultError(
    error: CapturedError
  ): error is CapturedApiFaultError {
    return error.subtype === ErrorSubtypes.FAULT;
  }

  /** Determine whether a captured error is a microservice fault error. */
  static isCapturedErrorMicroserviceFaultError(
    error: CapturedError
  ): error is CapturedApiFaultError {
    return error.subtype === ErrorSubtypes.SERVER;
  }

  /**
   * Determine whether a captured error is _related_ to an API call (i.e. an
   * "API error" or unhandled promise rejection).
   */
  static isCapturedErrorApiRelatedError(
    error: CapturedError
  ): error is CapturedApiError {
    return (
      error.type === ErrorTypes.API ||
      error.subtype === ErrorSubtypes.UNHANDLED_PROMISE
    );
  }

  /** Determine whether a HTTP response contains an API fault. */
  static isSuccessResponseWithFault(
    response: ApiFaultOrWarningsResponse | HttpResponse
  ): response is ApiFaultOrWarningsResponse {
    const typedResponse = response as ApiFaultOrWarningsResponse;
    return !!typedResponse?.data?.Fault;
  }

  /** Return a formatted code and subcode string from an API fault (e.g. "42" or "42-5"). */
  static getFaultCode(fault: ApiFault): string {
    return fault?.Code
      ? `${fault.Code}${fault?.Subcode ? `-${fault.Subcode}` : ''}`
      : '';
  }
}
